// src/components/BoardPack/pages/consolidate_boardpack/Budget_Actual_Chart_Page_v2.jsx
//
// Ye NAYA page hai. Purana `Budget_Actual_Chart_Page.jsx` bilkul waisa hi
// chhoda gaya hai — is file mein koi change nahi kiya.
//
// Wajah: real API response (Postman confirm) mein `data.ytd` (aur presumably
// `data.month` bhi) is tarah aata hai:
//
//   data.ytd = {
//     revenue: { steps: [...], endLabel: "Total Revenue", endValueRaw: -233676.54 },
//     cost:    { steps: [...], endLabel: "Total Cost",     endValueRaw: 135777.94 },
//   }
//
// Ye purane assumption (`startLabel/startValueRaw + steps + endLabel/endValueRaw`
// as ek flat block) se match nahi karta — ismein do independent bridges hain
// (revenue apne 0 se "Total Revenue" tak, cost apne 0 se "Total Cost" tak).
// Verify kiya: cost.steps ka sum === cost.endValueRaw (135,777.94) — matlab
// cost bridge revenue ke running total se bilkul independent hai.
//
// FINAL LAYOUT DECISION: revenue aur cost ko combine/chain NAHI karte.
// Dono independent series ke roop mein { revenue: [...], cost: [...] }
// component ki apni copy (`Budget_Actual_Chart_component_v2.jsx`) ko
// bhejte hain, jo unhe do alag mini-charts mein render karta hai (har
// ek apna khud ka 0 baseline).
import React, { useEffect, useRef, useState } from "react";
import { useQueue } from "@/context/QueueContext";
import { useNavigate } from "react-router-dom";
import ErrorState from "@/components/common/ErrorStateComponent";
import LoadingComponent from "@/components/common/LoadingComponent";
import { apiCall } from "@/api/apiCall";
import { processParams } from "../../utils/paramsHelper";
import { dynamicKey } from "../../utils/dynamicKey";
import Budget_Actual_Chart_component_v2 from "../../components/consolidate_boardpack_components/Budget_Actual_Chart_component_v2";

// Ek segment (revenue ya cost) ke `steps` array ko component-ready
// {label, value, type} shape mein convert karta hai.
const normalizeSteps = (steps = []) =>
  steps.map((s) => ({ label: s.label, value: s.raw, type: s.type }));

// Ek segment (revenue ya cost) ke `steps` + end total ko component-ready
// FLAT array mein convert karta hai — ye array khud apne 0 se shuru hoga.
const buildSegmentSeries = (segment) => {
  if (!segment) return [];

  return [
    ...normalizeSteps(segment.steps),
    { label: segment.endLabel, value: segment.endValueRaw, type: "total" },
  ];
};

// Ek period block (data.month ya data.ytd) ko { revenue, cost } shape mein
// handle karta hai — dono independent series hain, alag alag mini-charts
// mein render hongi (final decision: revenue aur cost do alag mini-charts,
// har ek apna 0 baseline).
const normalizeBridgeBlock = (block) => {
  if (!block) return { revenue: [], cost: [] };

  if (block.revenue || block.cost) {
    return {
      revenue: buildSegmentSeries(block.revenue),
      cost: buildSegmentSeries(block.cost),
    };
  }

  // fallback: purana flat shape (startLabel/steps/endLabel) — agar kabhi
  // aisa response aaya to poora usko "revenue" bucket mein daal dete hain
  return {
    revenue: [
      { label: block.startLabel, value: block.startValueRaw, type: "total" },
      ...normalizeSteps(block.steps),
      { label: block.endLabel, value: block.endValueRaw, type: "total" },
    ],
    cost: [],
  };
};

const Budget_Actual_Chart_Page_v2 = ({ year = "", month = "", contract = "", page = null, isRefresh = false }) => {
  const { enqueue } = useQueue();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [decSeries, setDecSeries] = useState({ revenue: [], cost: [] });
  const [ytdSeries, setYtdSeries] = useState({ revenue: [], cost: [] });
  const [meta, setMeta] = useState(null);
  const hasFetchedRef = useRef(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const apiParams = processParams(page?.params || {}, {
        year,
        month,
        contract,
        currency: "Reporting Currency",
        is_cfo: 0,
      });

      if (!page?.api) {
        throw new Error("Missing 'api' in page config — this chart's endpoint must be set explicitly.");
      }

      const response = await enqueue(() =>
        apiCall("GET", page.api, apiParams, navigate, false, false, isRefresh)
      );

      if (!response?.success) {
        throw new Error(response?.message || "API returned unsuccessful response");
      }

      // response.data.month / response.data.ytd — dono ab {revenue, cost} shape mein aate hain
      setDecSeries(normalizeBridgeBlock(response.data?.month));
      setYtdSeries(normalizeBridgeBlock(response.data?.ytd));
      setMeta(response.meta);
    } catch (err) {
      console.error("Budget_Actual_Chart (v2) fetch error:", err);
      setError(err.message || "Failed to load data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!year || !month || !contract) return;
    if (hasFetchedRef.current) return;

    hasFetchedRef.current = true;
    fetchData();
  }, [year, month, contract]);

  if (loading) {
    return <LoadingComponent />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={fetchData} />;
  }

  // Reference image ke naming pattern ke mutabiq: "{CONTRACT} {year} v {prevYear}
  // ACTUAL {month} [YTD] REVENUE/COST BRIDGE - REPORTING CURRENCY"
  const contractLabel = page?.params?.show_contract ?? meta?.contract ?? contract;
  const decBaseTitle = `MAWGIF ${contractLabel} ${meta?.year ?? year} v ${meta?.prevYear ?? ""} ACTUAL ${meta?.month ?? month}`.replace(/\s+/g, " ").trim();
  const ytdBaseTitle = `${decBaseTitle} YTD`;

  return (
    <Budget_Actual_Chart_component_v2
      title={dynamicKey({ text: page?.title, year, month, contract,meta})}
      subtitle={dynamicKey({ text: page?.subtitle, year, month, contract })}
      showComments={page?.showComments}
      decSeries={decSeries}
      ytdSeries={ytdSeries}
      decRevenueTitle={`${decBaseTitle} REVENUE BRIDGE - REPORTING CURRENCY`}
      decCostTitle={`${decBaseTitle} COST BRIDGE - REPORTING CURRENCY`}
      ytdRevenueTitle={`${ytdBaseTitle} REVENUE BRIDGE - REPORTING CURRENCY`}
      ytdCostTitle={`${ytdBaseTitle} COST BRIDGE - REPORTING CURRENCY`}
      logoSrc={meta?.logoUrl}
    />
  );
};
// (rendering: Budget_Actual_Chart_component_v2)

export default Budget_Actual_Chart_Page_v2;